import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "422",
  "slug": "what-map-projection-does-google-maps-use",
  "title": "What Map Projection Does Google Maps Use?",
  "excerpt": "Google Maps relies on Web Mercator, a modern twist on a 16th-century projection. Here is why it was chosen and the trade-off it makes with the size of continents.",
  "date": "2026-06-13",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1524661135-423995f22d0b?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHx3b3JsZCUyMG1hcCUyMGF0bGFzfGVufDF8MHx8fDE3ODEzMzg1MTR8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "Google Maps uses Web Mercator (EPSG:3857), a variant of the classic Mercator projection. It keeps north up and local angles correct for navigation, but stretches land near the poles, which is why Greenland looks far bigger than it really is.",
  "content": [
    {
      "type": "paragraph",
      "text": "If you have ever zoomed around Google Maps and wondered why Greenland looks roughly the size of Africa when it is actually about fourteen times smaller, the answer is hiding in the projection. Every flat map has to make a compromise, because you cannot wrap a sphere onto a rectangle without distorting something. The compromise Google chose shapes how billions of people picture the world."
    },
    {
      "type": "heading",
      "text": "The Short Answer: Web Mercator"
    },
    {
      "type": "paragraph",
      "text": "Google Maps uses a projection called Web Mercator, sometimes labelled WGS 84 / Pseudo-Mercator or by its technical identifier EPSG:3857. It is a close cousin of the Mercator projection that the Flemish cartographer Gerardus Mercator published in 1569. The classic Mercator was built for sea navigation, and the web version keeps the same headline feature: a straight line drawn between two points holds a constant compass bearing, so a sailor could steer one heading and trust it."
    },
    {
      "type": "paragraph",
      "text": "The main difference is mathematical. Traditional Mercator treats the Earth as a slightly squashed ellipsoid, which is closer to its true shape, while Web Mercator treats it as a perfect sphere when it does the projecting. That shortcut introduces small positional errors, never more than a tiny fraction of a percent, but it makes the math fast enough to render millions of map tiles on demand. For an interactive map that has to redraw every time you pan or zoom, speed wins."
    },
    {
      "type": "heading",
      "text": "Why Mercator, of All Projections?"
    },
    {
      "type": "paragraph",
      "text": "Mercator gets a lot of criticism, so why would the most-used map on Earth pick it? Because it solves the specific problems a navigation app actually cares about:"
    },
    {
      "type": "list",
      "items": [
        "It is conformal, meaning it preserves the shape of small areas and the angles between roads. A street corner looks like a right angle, which matters when you are following turn-by-turn directions.",
        "North is always straight up and the grid is square, so the map can be sliced into identical square tiles that line up perfectly at every zoom level.",
        "Those square tiles make caching and streaming efficient, which is why panning and zooming feel instant even on a phone with a weak connection."
      ]
    },
    {
      "type": "paragraph",
      "text": "For a tool whose core job is getting you from A to B at street level, preserving local shape beats preserving the relative size of continents you will never drive across. The projection is optimised for the scale at which people use it most: a few blocks at a time."
    },
    {
      "type": "heading",
      "text": "The Trade-Off: Distorted Sizes"
    },
    {
      "type": "paragraph",
      "text": "Mercator buys its tidy angles by stretching everything as you move away from the equator. The further north or south you go, the more land is inflated. Near the poles the distortion becomes extreme, which is why Greenland, Canada, Russia, and Antarctica all look enormous compared with countries near the equator."
    },
    {
      "type": "paragraph",
      "text": "A few familiar illusions come straight from this stretch. Greenland appears similar in size to Africa despite being a fraction of it. Alaska looks comparable to Brazil when Brazil is roughly five times larger. Scandinavia and Russia both swell well beyond their true footprint. Web Mercator also cannot show the poles at all: the projection runs to infinity at 90 degrees, so Google quietly clips the map at roughly 85 degrees north and south, which is why you can never quite slide all the way to the top of the world."
    },
    {
      "type": "heading",
      "text": "Why Google Maps Now Shows a Globe"
    },
    {
      "type": "paragraph",
      "text": "There is a twist worth knowing. When you zoom all the way out on Google Maps in a desktop browser, the flat map melts into a 3D globe. Google introduced this globe view in 2018 specifically to fix the size problem at a global scale, so Greenland finally shrinks to its real proportions and the continents regain their honest shapes. Zoom back in, though, and the map snaps back to flat Web Mercator, because that is what works for navigation up close. It is a neat admission that no single projection is right at every scale."
    },
    {
      "type": "heading",
      "text": "It Is Not Just Google"
    },
    {
      "type": "paragraph",
      "text": "Web Mercator is effectively the default language of online maps. OpenStreetMap, Bing Maps, Mapbox, and most satellite-tile services all use it, which is why their tiles can be mixed and matched without anything sliding out of alignment. Once one major platform standardised on it, sharing map data became far easier, and the projection became a de facto web standard rather than a deliberate cartographic statement. The result is that an entire generation has learned the shape of the world through Mercator-tinted glasses."
    }
  ]
};
